import { Injectable } from '@angular/core';
import { IAddressData, Address } from '@df/session/api/address-book/address';
import { ApplePay } from '@df/module-apple-pay/apple-pay.namespace';
import { AddressService } from '@df/session/api/address-book/address.service';

@Injectable({ providedIn: 'root' })
export class ApplePayService {
    private _isAvailable?: boolean = undefined;

    applePaySession = typeof window === 'undefined' ? undefined : window.ApplePaySession;

    constructor(private addressService: AddressService) {}
    /**
     * Checks if apple pay is available in browser
     * @returns {boolean}
     */
    get isAvailable(): boolean {
        if (this._isAvailable === undefined) {
            this._isAvailable = !!this.applePaySession && this.applePaySession.canMakePayments();
        }
        return this._isAvailable;
    }

    /**
     * Checks if apple pay version is suppoerted in browser
     * @param {number} version
     * @returns {boolean}
     */
    isSupportedVersion(version: number): boolean {
        return !!this.applePaySession && this.applePaySession.supportsVersion(version);
    }

    /**
     * converts address to apple contact
     * @param {IAddressData} address
     * @returns {ApplePay.IContact}
     */
    getContactFromAddress(address: IAddressData): ApplePay.IContact {
        const regionId = address.region_id;
        const countryId = address.country_id;
        const region = regionId && countryId ? this.addressService.getCountryRegionFromCountryId(countryId, regionId) : undefined;

        return {
            phoneNumber: address.telephone,
            givenName: address.firstname,
            familyName: address.lastname,
            addressLines: [address.street_1, address.street_2, address.street_3].filter(i => !!i) as string[],
            locality: address.city,
            postalCode: address.postcode,
            administrativeArea: region?.code,
            countryCode: address.country_id?.toUpperCase()
        };
    }

    /**
     * Converts apple contact to address
     * @param {ApplePay.IContact} contact
     * @returns {IAddressData}
     */
    getAddressFromContact(contact: ApplePay.IContact): IAddressData {
        const countryId = contact.countryCode;
        const regionCode = contact.administrativeArea;
        const region =
            countryId && regionCode ? this.addressService.getCountryRegionFromCountryId(countryId, undefined, regionCode) : undefined;

        return {
            telephone: contact.phoneNumber,
            firstname: contact.givenName,
            lastname: contact.familyName,
            street_1: contact.addressLines?.[0],
            street_2: contact.addressLines?.[1],
            street_3: contact.addressLines?.[2],
            city: contact.locality,
            postcode: contact.postalCode,
            region_id: region?.id,
            region: region?.id ? '' : regionCode,
            country_id: countryId?.toUpperCase(),
            valid: true
        };
    }

    /**
     * Returns which contact fields errors we can use depending on event and errorCode
     * @param {ApplePay.ErrorCode.shippingContactInvalid | ApplePay.ErrorCode.billingContactInvalid} erorrCode
     * @param {ApplePay.ErrorContext} context
     */
    getAvailableContactErrorFields(
        erorrCode: ApplePay.ErrorCode.shippingContactInvalid | ApplePay.ErrorCode.billingContactInvalid,
        context: ApplePay.ErrorContext
    ): (keyof ApplePay.IContact | ApplePay.ContactField)[] {
        const shippingSelectionErrorFields: (keyof ApplePay.IContact | ApplePay.ContactField)[] = [
            ApplePay.ContactField.postalAddress,
            'locality',
            'subLocality',
            'postalCode',
            'administrativeArea',
            'subAdministrativeArea',
            'country',
            'countryCode'
        ];
        if (erorrCode === ApplePay.ErrorCode.shippingContactInvalid) {
            if (context === ApplePay.ErrorContext.completeShippingContactSelection) {
                return shippingSelectionErrorFields;
            } else {
                return [...shippingSelectionErrorFields, 'addressLines', ApplePay.ContactField.name, 'emailAddress', 'phoneNumber'];
            }
        } else {
            if (context === ApplePay.ErrorContext.completePayment) {
                return [...shippingSelectionErrorFields, 'addressLines', ApplePay.ContactField.name];
            }
        }
        return [];
    }
}
